import { useState, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useGetStatesQuery } from 'redux/services/states'
import { useTranslation } from 'next-i18next'

import { STRIPE_COUNTRY_CODES } from 'utils/constants/misc'
import { useInputEventDebounce } from 'utils/hooks/useInputEventDebounce'
import { useGoogleAutoComplete } from './utils'

export interface FieldIdsConfig {
  country: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  zipCode: string
}

// Union type for field ID configurations (keeping for backward compatibility)
export type AddressFieldIds = FieldIdsConfig

export interface UseAddressFormOptions {
  fieldIds: FieldIdsConfig
  enableLine2?: boolean
  watchCountry?: string
}

export const useAddressForm = ({
  fieldIds,
  enableLine2 = true,
  watchCountry: externalWatchCountry,
}: UseAddressFormOptions) => {
  const [showLine2Input, setShowLine2Input] = useState(false)
  const [autoComplete, setAutoComplete] = useState('')

  const form = useFormContext()
  const { t } = useTranslation()

  // Use external watchCountry if provided, otherwise watch from form
  const watchCountry = externalWatchCountry || form.watch(fieldIds.country) || 'US'
  const watchState = form.watch(fieldIds.state)

  // Get form errors
  const fieldError = (fieldName: string) =>
    form.formState.errors[fieldName] as { message: string } | undefined

  // Event handlers
  const handleAddLine2Click = () => {
    setShowLine2Input(true)
  }

  const handleAddLine2KeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setShowLine2Input(true)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handleOnFocus = () => {
    // This is a workaround to prevent the browser from auto-filling the address field
    // when the user selects an address from the Places Autocomplete widget.
    // Chrome won't accept the 'off' value, so we use a non-standard value instead.
    setAutoComplete('non-standard-value')
  }

  const handleOnBlur = () => {
    // When the field loses focus, we reset the autoComplete attribute to its default value.
    // This allows the browser to auto-fill the address field when the user selects an address
    // through the other fields in the form.
    setAutoComplete('')
  }

  // States query
  const {
    data: statesData,
    isFetching: statesIsFetching,
    error: statesError,
  } = useGetStatesQuery({
    countryCode: watchCountry,
  })

  // Debounce Google Places API calls
  useInputEventDebounce(fieldIds.addressLine1, { ms: 400 })

  // Google Places autocomplete integration
  const { ref } = useGoogleAutoComplete({
    enableLine2,
    setShowLine2Input: (bool) => setShowLine2Input(bool || false),
    setAddress1: (line1) => form.setValue(fieldIds.addressLine1, line1),
    setAddress2: enableLine2 ? (line2) => form.setValue(fieldIds.addressLine2, line2) : null,
    setCity: (city) => form.setValue(fieldIds.city, city),
    setState: (state) => form.setValue(fieldIds.state, state),
    setCountry: (country) => form.setValue(fieldIds.country, country),
    setZipcode: (zipCode) => form.setValue(fieldIds.zipCode, zipCode),
    triggerValidation: () =>
      Object.values(fieldIds).forEach((id) => {
        form.trigger(id)
      }),
  })

  // Country options
  const countryOptions = useMemo(() => {
    return [
      { label: '', value: '' },
      ...STRIPE_COUNTRY_CODES.map((country) => ({
        label: country.country,
        value: country.code,
      })),
    ]
  }, [])

  // State options for the original address.tsx format (uses different mapping)
  const stateOptions = useMemo(() => {
    if (statesIsFetching) return []
    if (statesError || !statesData) return []

    const options = statesData.map((state) => ({
      value: state.isoCode,
      label: state.name,
    }))

    options.unshift({
      value: '',
      label: t('OrderAddress.label.pleaseSelect', 'Please select'),
    })

    return options
  }, [statesData, statesIsFetching, statesError, t])

  // Helper function to get localized state label
  const getStateLabel = () => {
    switch (watchCountry) {
      case 'US':
        return t('OrderAddress.label.state', 'State')
      case 'CA':
        return t('OrderAddress.label.province', 'Province')
      default:
        return t('OrderAddress.label.region', 'Region')
    }
  }

  return {
    // State
    showLine2Input,
    setShowLine2Input,
    autoComplete,
    watchCountry,
    watchState,

    // Event handlers
    handleAddLine2Click,
    handleAddLine2KeyPress,
    handleKeyDown,
    handleOnFocus,
    handleOnBlur,

    // Form utilities
    fieldError,

    // Google Places
    googlePlacesRef: ref,

    // Data
    countryOptions,
    stateOptions,
    statesIsFetching,
    statesError,
    statesData,

    // Helpers
    getStateLabel,
    hasStates: (statesData?.length ?? 0) > 0,
  }
}
